/** Mieter (Screen laden/Mieter): Liste mit Filtern, Anlegen. Nachricht/Excel-Import sind V2. */
import { useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router";
import { Button, Dialog, EmptyState, Notice, Seitenzustand, StatusBadge, TextField } from "../ui";
import { Card, FormGrid, PageHeader, Select, Textarea } from "../ui/Layout";
import { ApiFehler } from "../api/client";
import { formatEuro } from "@shared/geld";
import { formatTag } from "@shared/zeit";
import { useMieterAendern, useMieterAnlegen, useMieterListe, useNaechsteMieternummer, type MieterDetail, type MieterEingabe, type MieterZeile } from "./mieter-api";

export const LAGE_TEXT: Record<MieterZeile["lage"], { badge: "paid" | "attention" | "wait" | "neutral" | "error"; text: string }> = {
  aktiv: { badge: "paid", text: "Aktiv" },
  endet_bald: { badge: "attention", text: "Endet bald" },
  neu: { badge: "wait", text: "Neu" },
  ehemalig: { badge: "neutral", text: "Ehemalig" },
  gesperrt: { badge: "error", text: "Gesperrt" },
  archiviert: { badge: "neutral", text: "Archiviert" },
};

export function initialen(name: string): string {
  return name.split(/\s+/).map((t) => t[0] ?? "").join("").slice(0, 2).toUpperCase();
}

export function Mieter() {
  const q = useMieterListe();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<"aktiv" | "ehemalig" | "gesperrt" | "alle">("aktiv");
  const [art, setArt] = useState<"" | "privat" | "gewerblich">("");
  const [suche, setSuche] = useState("");
  const [offen, setOffen] = useState(false);
  const liste = q.data?.mieter ?? [];
  const gruppen = useMemo(
    () => ({
      aktiv: liste.filter((m) => ["aktiv", "endet_bald", "neu"].includes(m.lage)),
      ehemalig: liste.filter((m) => m.lage === "ehemalig" || m.lage === "archiviert"),
      gesperrt: liste.filter((m) => m.lage === "gesperrt"),
    }),
    [liste],
  );
  const basis = filter === "alle" ? liste : gruppen[filter];
  const gefiltert = basis.filter((m) => {
    if (art === "privat" && m.gewerblich) return false;
    if (art === "gewerblich" && !m.gewerblich) return false;
    if (suche) {
      const s = suche.toLowerCase();
      if (!m.name.toLowerCase().includes(s) && !m.mieternummer.includes(s) && !m.regale.some((r) => r.includes(s)) && !m.email.toLowerCase().includes(s)) return false;
    }
    return true;
  });
  const guthaben = gefiltert.reduce((a, m) => a + m.guthabenCent, 0);

  return (
    <>
      <PageHeader kicker="Mieter" title="Mieter" sub={`${gruppen.aktiv.length} aktiv · ${gruppen.ehemalig.length} ehemalig · ${gruppen.gesperrt.length} gesperrt`} actions={<Button variant="primary" icon="plus" onClick={() => setOffen(true)}>Mieter anlegen</Button>} />
      <div className="sc-toolbar" style={{ marginBottom: "var(--space-4)" }}>
        {(
          [
            ["aktiv", `Aktiv ${gruppen.aktiv.length}`],
            ["ehemalig", `Ehemalig ${gruppen.ehemalig.length}`],
            ["gesperrt", `Gesperrt ${gruppen.gesperrt.length}`],
            ["alle", "Alle"],
          ] as [typeof filter, string][]
        ).map(([k, t]) => (
          <button key={k} type="button" className="sc-pill" aria-pressed={filter === k} onClick={() => setFilter(k)}>{t}</button>
        ))}
        <span className="sc-grow" />
        <Select label="Art" value={art} onChange={(e) => setArt(e.target.value as typeof art)}>
          <option value="">Privat und gewerblich</option>
          <option value="privat">Nur privat</option>
          <option value="gewerblich">Nur gewerblich</option>
        </Select>
        <TextField label="Suche" icon="search" value={suche} onChange={(e) => setSuche(e.target.value)} placeholder="Name, Nr. oder Regal" />
      </div>
      <Seitenzustand query={q} istLeer={(d) => d.mieter.length === 0} leer={<EmptyState illustration="empty-shelf" title="Noch keine Mieter" action={<Button variant="primary" icon="plus" onClick={() => setOffen(true)}>Ersten Mieter anlegen</Button>}>Die Mieternummer (vierstellig, ab 1001) steht vorn im Artikelcode.</EmptyState>}>
        {() => (
          <Card flush>
            <div className="app-tabellen-scroll">
              <table className="rg-table">
                <thead><tr><th>Name</th><th>Nr.</th><th>Regal</th><th>Mietende</th><th>Status</th><th className="is-num">Guthaben</th><th className="is-num">Artikel</th><th>Art</th></tr></thead>
                <tbody>
                  {gefiltert.length === 0 ? <tr><td colSpan={8} className="rg-table__empty">Kein Mieter passt zu den Filtern.</td></tr> : null}
                  {gefiltert.map((m) => {
                    const l = LAGE_TEXT[m.lage];
                    return (
                      <tr key={m.id} className="rg-row--click" onClick={() => navigate(`/laden/mieter/${m.id}`)}>
                        <td><span className="sc-row"><span className="sc-avatar">{initialen(m.name)}</span><span className="sc-col" style={{ gap: 0 }}><Link to={`/laden/mieter/${m.id}`} className="sc-strong" onClick={(e) => e.stopPropagation()}>{m.name}</Link><span className="sc-muted sc-small">{m.email || "ohne Portal"}</span></span></span></td>
                        <td className="sc-num">{m.mieternummer}</td>
                        <td>{m.regale.join(", ") || "–"}</td>
                        <td>{m.mietende ? formatTag(m.mietende) : m.regale.length ? "Abo" : "–"}</td>
                        <td><StatusBadge status={l.badge}>{l.text}</StatusBadge></td>
                        <td className="is-num">{formatEuro(m.guthabenCent)}</td>
                        <td className="is-num">{m.artikelImRegal}</td>
                        <td>{m.gewerblich ? "gewerblich" : "privat"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="sc-muted sc-small" style={{ padding: "var(--space-3) var(--space-4)" }}>{gefiltert.length} Mieter · Guthaben gesamt {formatEuro(guthaben)}</p>
          </Card>
        )}
      </Seitenzustand>
      {offen ? <MieterFormular onClose={() => setOffen(false)} onAngelegt={(id) => navigate(`/laden/mieter/${id}`)} /> : null}
    </>
  );
}

/** Dialog Mieter anlegen/bearbeiten (auch aus „Buchung anlegen“ und Mieter-Detail). */
export function MieterFormular({ mieter, onClose, onAngelegt }: { mieter?: MieterDetail["mieter"]; onClose: () => void; onAngelegt?: (id: string, name: string, email: string) => void }) {
  const anlegen = useMieterAnlegen();
  const aendern = useMieterAendern();
  const naechste = useNaechsteMieternummer(!mieter);
  const [form, setForm] = useState({
    name: mieter?.name ?? "",
    email: mieter?.email ?? "",
    telefon: mieter?.telefon ?? "",
    strasse: mieter?.adresse.strasse ?? "",
    plz: mieter?.adresse.plz ?? "",
    ort: mieter?.adresse.ort ?? "",
    gewerblich: mieter?.gewerblich ?? false,
    ustId: mieter?.ustId ?? "",
    iban: "",
    ibanLoeschen: false,
    kontoinhaber: mieter?.kontoinhaber ?? "",
    auszahlungsweg: (mieter?.auszahlungsweg ?? "ueberweisung") as "ueberweisung" | "bar",
    interneNotiz: mieter?.interneNotiz ?? "",
    mieternummer: mieter?.mieternummer ?? "",
    geworbenVon: mieter?.geworbenVon ?? "",
  });
  const [fehler, setFehler] = useState<string | null>(null);

  async function absenden(e: FormEvent) {
    e.preventDefault();
    setFehler(null);
    if (form.name.trim().length < 2) return setFehler("Bitte einen Namen eingeben.");
    if (!mieter && form.mieternummer && !/^\d{4}$/.test(form.mieternummer)) return setFehler("Mieternummer ist vierstellig, z. B. 1234.");
    const d: MieterEingabe = {
      name: form.name.trim(),
      email: form.email.trim().toLowerCase(),
      telefon: form.telefon.trim(),
      adresse: { strasse: form.strasse.trim(), plz: form.plz.trim(), ort: form.ort.trim() },
      gewerblich: form.gewerblich,
      ustId: form.ustId.trim(),
      iban: form.ibanLoeschen ? null : form.iban.trim() || undefined,
      kontoinhaber: form.kontoinhaber.trim(),
      auszahlungsweg: form.auszahlungsweg,
      interneNotiz: form.interneNotiz,
      geworbenVon: form.geworbenVon.trim(),
      mieternummer: !mieter && form.mieternummer ? form.mieternummer : undefined,
    };
    try {
      if (mieter) {
        await aendern.mutateAsync({ id: mieter.id, ...d });
        onClose();
      } else {
        const r = await anlegen.mutateAsync(d);
        onClose();
        onAngelegt?.(r.id, d.name, d.email);
      }
    } catch (err) {
      setFehler(err instanceof ApiFehler ? err.message : "Speichern hat nicht geklappt.");
    }
  }

  return (
    <Dialog open title={mieter ? "Stammdaten bearbeiten" : "Mieter anlegen"} onClose={onClose} actions={<><Button variant="quiet" onClick={onClose}>Abbrechen</Button><Button variant="primary" type="submit" form="mieter-form" loading={anlegen.isPending || aendern.isPending}>{mieter ? "Speichern" : "Anlegen"}</Button></>}>
      <form id="mieter-form" className="app-form" onSubmit={absenden} noValidate>
        {fehler ? <Notice tone="error">{fehler}</Notice> : null}
        <FormGrid>
          <TextField label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus />
          <TextField label="Mieternummer" value={mieter ? mieter.mieternummer : form.mieternummer} onChange={(e) => setForm({ ...form, mieternummer: e.target.value })} inputMode="numeric" placeholder={naechste.data?.mieternummer ?? "1234"} disabled={!!mieter} hint={mieter ? "Steht auf Etiketten, nicht änderbar" : `Leer = automatisch ${naechste.data?.mieternummer ?? ""}`} />
          <TextField label="Geworben von (Mieternummer)" value={form.geworbenVon} onChange={(e) => setForm({ ...form, geworbenVon: e.target.value })} inputMode="numeric" placeholder="leer = niemand" hint="Freunde werben: Die werbende Person sieht im Portal, wie viele sie geworben hat" />
          <TextField label="E-Mail (Mieterportal)" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} hint="Ohne E-Mail: kein Portal, Vertrag nur vor Ort" />
          <TextField label="Telefon" value={form.telefon} onChange={(e) => setForm({ ...form, telefon: e.target.value })} inputMode="tel" />
          <TextField label="Straße und Hausnummer" value={form.strasse} onChange={(e) => setForm({ ...form, strasse: e.target.value })} />
          <TextField label="PLZ" value={form.plz} onChange={(e) => setForm({ ...form, plz: e.target.value })} inputMode="numeric" />
          <TextField label="Ort" value={form.ort} onChange={(e) => setForm({ ...form, ort: e.target.value })} />
        </FormGrid>
        <label className="sc-check"><input type="checkbox" checked={form.gewerblich} onChange={(e) => setForm({ ...form, gewerblich: e.target.checked })} /> Verkauft gewerblich</label>
        {form.gewerblich ? <TextField label="USt-IdNr. (optional)" value={form.ustId} onChange={(e) => setForm({ ...form, ustId: e.target.value })} /> : null}
        <h3 className="heading">Auszahlung</h3>
        <FormGrid>
          <Select label="Auszahlungsweg" value={form.auszahlungsweg} onChange={(e) => setForm({ ...form, auszahlungsweg: e.target.value as "ueberweisung" | "bar" })}>
            <option value="ueberweisung">Überweisung</option>
            <option value="bar">Bar im Laden</option>
          </Select>
          <TextField label="Kontoinhaber" value={form.kontoinhaber} onChange={(e) => setForm({ ...form, kontoinhaber: e.target.value })} />
          <TextField label={mieter?.ibanMaske ? `IBAN (hinterlegt: ${mieter.ibanMaske})` : "IBAN"} value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value, ibanLoeschen: false })} placeholder={mieter?.ibanMaske ? "Neue IBAN nur bei Änderung" : "DE89 3704 0044 0532 0130 00"} autoComplete="off" hint="Wird verschlüsselt gespeichert, Anzeige nur maskiert" disabled={form.ibanLoeschen} />
        </FormGrid>
        {mieter?.ibanMaske ? <label className="sc-check"><input type="checkbox" checked={form.ibanLoeschen} onChange={(e) => setForm({ ...form, ibanLoeschen: e.target.checked, iban: "" })} /> Hinterlegte IBAN löschen</label> : null}
        <Textarea label="Interne Notiz (für den Mieter nicht sichtbar)" value={form.interneNotiz} onChange={(e) => setForm({ ...form, interneNotiz: e.target.value })} rows={3} />
      </form>
    </Dialog>
  );
}
